<template>
  <div class="patient-card no-print"
       v-if="!treatmentCard">
    <div class="text-center" v-if="!patientLoaded" >
      <b-spinner label="Spinning"></b-spinner>
    </div>
    <div class="row"
         v-if="patient">
      <div class="col-md-12 col-12">
        <div class="row">
          <div class="text-center col-12 col-lg-auto">
            <PatientImg class="p-img"/>
          </div>
          <div class="col-md col-12">
            <h2 class="text-lg-left text-center">
              <span>{{ patient.full_name }}</span>
              <span class="ml-2 small font-weight-normal"
                    v-if="patient.gender_string !== null">
                  ({{ $t(patient.gender_string+'_short') }})
                </span>
              <PatientTags class="ml-2"
                           v-if="!isPatient"
                           :editable="true"
                           @toggleTag="toggleTag"
                           :patient="patient"/>
              <b-dropdown right variant="link"
                          no-caret
                          v-if="patientTags && tags && (patientTags.length < tags.length) && !isPatient">
                <template #button-content>
                  <a class="pointer position-relative" style="top: -3px;"><PlusSvg/></a>
                </template>
                <div class="pt-0 dropdown-overscroll smaller">
                  <template v-for="(t, tind) in tags">
                    <a class="dropdown-item pointer"
                       :key="`tag-${tind}`"
                       @click="toggleTag(t, 'add')"
                       v-if="!patientTags.includes(t.id)">
                      <span class="circle mr-2" :style="`background-color: ${t.color}`"></span>
                      {{ t.title }}
                    </a>
                  </template>
                </div>
              </b-dropdown>
            </h2>
            <div v-if="patient.clientStatus">
                <span class="badge" :class="'badge-'+patient.clientStatus">
                {{ $t(patient.clientStatus) }}
                </span>
              <span v-if="patient.telegram_chat"
                    v-b-tooltip.hover
                    class="telegram-bot-icon ml-2"
                    :title="$t('connected_to_telegram_bot')">
                  <img src="/images/general/telegram_bot.png" alt="telegram_bot"/>
                </span>
            </div>
            <div>
              <div class="row">
                <div class="col-md-6">
                  <div v-if="patient.blocked == 1">
                    <div class="d-inline-block text-danger font-weight-bold">{{ $t('patient_deleted') }}</div>
                    <a class="pointer text-underlined ml-2 text-lowercase"
                       tabindex="#"
                       @click="restorePatient">({{ $t('restore') }})</a>
                  </div>
                  <!--                <div><span class="text-muted">{{ $t('activity') }}:</span> {{ $t(patient.activity_status) }}</div>-->
                  <!--                <div v-if="patient.activity_status">{{ $t(patient.activity_status) }}</div>-->
                  <div v-if="Number(clinic.country_id) === countries.KAZAKHSTAN && patient.iin">{{ $t('iin') }}: {{ patient.iin }}</div>
                  <div>
                    {{ $t('years') }}: {{ patient.age || patient.age === 0 ? patient.age : '-' }}
                    <span class="text-muted" v-if="patient.date_of_birth">({{ formatDate(patient.date_of_birth) }})</span>
                  </div>
                  <div v-if="patient.patient_condition">{{ $t('condition') }}: {{ patient.patient_condition.title }}</div>
                  <div v-if="patient.description">{{ patient.description }}</div>
                </div>
                <div class="col-md-6">
                  <div v-if="patient.url">
                    <a target="_blank"
                       :href="patient.url"
                       ref="nofollow">
                      {{ patient.url }}
                    </a>
                  </div>
                  <div class="font-italic">{{ $t('source') }}: {{ patient.sourceName}}</div>
                </div>
              </div>
            </div>
          </div>
          <div class="mt-3 mt-lg-0 col-lg-6 col-xl-6">
            <div class="row justify-content-xl-end">
              <div class="col-auto col-md-6 col-lg-auto">
                <div v-if="patient.mphone && !rights.includes('hide_patient_phone')">
                  <OutgoingCallElement :phone="patient.mphone"
                                       color="black"
                                       class="mphone"/>
                  <a class="ml-3"
                     :href="`tel:${patient.mphone}`"
                     v-b-tooltip.hover
                     :title="$t('call')">
                    <PhoneSvg class="svg-primary"/>
                  </a>
                  <a v-if="clinic && clinic.sms_info && rights.includes('sms_send')"
                     v-b-tooltip.hover
                     :title="$t('send_sms')"
                     class="ml-3 pointer"
                     @click="smsModal(patient.mphone)">
                    <SmsSvg class="svg-primary"/>
                  </a>

                  <WhatsappTelegramIcon :patient-id="patient.id" :phone="patient.phone"/>

                  <div class="phone-comment" v-if="patient.mphone_descr">{{ patient.mphone_descr }}</div>
                </div>

                <div v-if="patient.email || patient.phone" class="mt-md-2 mt-0 d-flex flex-row align-items-center">
                  <div v-if="patient.email" class="d-inline-block">
                    <a @click="sendMail"
                       class="link pointer text-primary"
                       v-b-tooltip.hover
                       :title="$t('send_mail')"
                       v-if="rights.includes('emailing')">
                      {{ patient.email }}
                    </a>
                    <span v-else>{{ patient.email }}</span>
                  </div>
                  <a class="pointer"
                     :class="{ 'ml-3': patient.email }"
                     v-b-tooltip.hover
                     v-if="(rights.includes('crm') || rights.includes('jet_api')) && (patient.email || patient.phone)"
                     :title="$t('plan_notification')"
                     @click="$bvModal.show('deferred-message-modal')">
                    <ClockSvg class="svg-primary"/>
                  </a>

                  <PatientAddToFunnelBtn :patient="patient"/>

                </div>

              </div>
              <div class="mt-md-0 mt-3 col-auto col-md-6 col-lg-auto" v-if="patient.mphone2 && !rights.includes('hide_patient_phone')">
                <div>
                  <div class="left-separator"></div>
                  <div class="d-inline-block">
                    <OutgoingCallElement :phone="patient.mphone2"
                                         color="black"
                                         class="mphone"/>
                    <a class="ml-3"
                       :href="`tel:${patient.mphone2}`"
                       v-b-tooltip.hover
                       :title="$t('call')">
                      <PhoneSvg class="svg-primary"/>
                    </a>
                    <a v-if="clinic && clinic.sms_info && rights.includes('sms_send')"
                       v-b-tooltip.hover
                       :title="$t('send_sms')"
                       class="ml-3 pointer"
                       @click="smsModal(patient.mphone2)">
                      <SmsSvg class="svg-primary"/>
                    </a>
                    <WhatsappTelegramIcon :patient-id="patient.id" :phone="patient.mphone2"/>

                    <div class="phone-comment" v-if="patient.mphone2_descr">{{ patient.mphone2_descr }}</div>
                  </div>
                </div>
              </div>
            </div>
            <PatientAddTask :patient="patient"
                            v-if="rights.includes('tasks') && patient"
                            class="mt-4"/>
          </div>
        </div>
      </div>
      <div class="mt-3 col-md-12 col-12">
        <a class="d-block d-md-none more-info-link collapse-btn-arrow"
           v-spaceduck-collapse="`collapse-patient-info`">
          {{ $t('detailed_information') }}
        </a>
        <spaceduck-collapse id="collapse-patient-info" :visible="!isMobile">
          <div class="row">
            <div class="mb-md-0 mb-3 col-md col-12">
              <div class="card-p">
                <PatientColInline label="card_number" :title="patient.card"/>
                <PatientColInline v-if="patient.activity_status" label="activity" :title="$t(patient.activity_status)"/>
                <PatientColInline label="treating_doctor" :title="patient.doctor ? patient.doctor.full_name : '-'"/>
                <PatientColInline label="discount" :title="((patient.discount ? patient.discount : 0) + '%')"/>
              </div>
            </div>
            <div class="mb-md-0 mb-3 col-md col-12" v-if="rights.includes('payments')">
              <div class="card-p">
                <PatientColInline v-if="usesBonuses"
                                  label="bonuses"
                                  :title="patient.use_bonuses ? formatCurrency(patient.total_bonuses) : $t('does_not_participate')">
                  <a class="pointer ml-2 position-relative"
                     style="top: -2px;"
                     v-b-tooltip.hover
                     :title="$t('bonus_adjustment')"
                     @click="bonusRefill"
                     v-if="rights.includes('bonuses') && patient.use_bonuses">
                    <DollarSvg class="svg-light"/>
                  </a>
                </PatientColInline>
                <PatientColInline label="balance"
                                  v-if="rights.includes('deposit')"
                                  :title="formatCurrency(patient.advance_calculated)">
                  <a class="pointer ml-2 position-relative"
                     v-b-tooltip.hover
                     style="top: -2px;"
                     :title="$t('balance_refill')"
                     v-if="rights.includes('payments')"
                     @click="balanceRefill">
                    <DollarSvg class="svg-light"/>
                  </a>
                  <b-dropdown variant="text"
                              class="ml-2"
                              toggle-class="line-height-normal border-0 font-size-0"
                              menu-class="px-3"
                              toggle-tag="a"
                              v-if="patient.deposit_by_accounts && patient.deposit_by_accounts.length">
                    <template #button-content>
                      <InfoSvg v-b-tooltip.hover
                               :title="$t('deposit_accounts')"
                               class="svg-size-lg svg-light position-relative"
                               style="top: -2px;"/>
                    </template>
                    <DepositByAccountsTable :patient="patient" size="md"/>
                  </b-dropdown>
                </PatientColInline>
                <PatientColInline label="total_paid"
                                  :title="formatCurrency(patient.totalPaidBranch)"/>
                <PatientColInline :label="debtForBranch > 0 ? 'overpayment' : 'debt'"
                                  :class="{ 'text-danger': debtForBranch < 0, 'text-success': debtForBranch > 0 }"
                                  :title="formatCurrency(debtForBranch)">
                  <a v-if="debtForBranch < 0 && rights.includes('payments')"
                     v-b-tooltip.hover
                     :title="$t('payment')"
                     style="top: -2px;"
                     class="ml-2 pointer position-relative"
                     @click="paymentPressed">
                    <DollarSvg class="svg-light"/>
                  </a>
                </PatientColInline>
              </div>
            </div>
            <div class="mb-md-0 mb-3 col-md col-12">
              <div class="card-p">
                <PatientColInline label="last_visit"
                                  :title="patient.lastVisit ? formatDate(patient.lastVisit.start, 'DD.MM.YYYY, HH:mm') : '-'"/>
                <PatientColInline label="next_visit"
                                  :title="patient.nextVisit ? formatDate(patient.nextVisit.start, 'DD.MM.YYYY, HH:mm') : '-'"/>
                <PatientColInline label="curator"
                                  :title="patient.curator ? patient.curator.full_name : '-'"/>
              </div>
            </div>
          </div>
          <div class="row mt-md-4 mt-0">
            <div class="col-md-4" v-if="rights.includes('laboratories')">
              <div class="card-p">
                <label class="form-label">{{ $t('active_requirement_orders') }}</label>
                <div v-if="patient.activeLaboratoryOrders">
                  <div v-if="!patient.activeLaboratoryOrders.length">-</div>
                  <div v-for="order in patient.activeLaboratoryOrders"
                       class="visit-line word-break"
                       :key="`order-${order.id}`">
                    {{ $t('requirement_order') }} #{{ order.id }}
                    {{ order.laboratory ? order.laboratory.title : '-' }}
                    ({{ $t(order.status) }} <span class="circle" :class="`status-${order.status}`"></span>)
                    <div class="action-buttons-div">
                      <LaboratoryOrdersDatatableActions class="ml-3" :object="order"/>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div :class="{
              'col-md-8': rights.includes('laboratories'),
              'col-md-12': !rights.includes('laboratories'),
            }"
                 class="mt-md-0 mb-md-0 mt-3 mb-3">
              <div class="card-p">
                <label class="form-label">{{ $t('active_plan_treatments') }}</label>
                <div>
                  <div v-if="!patient.activePlanTreatments || !patient.activePlanTreatments.length">-</div>
                  <div v-if="patient.activePlanTreatments && patient.activePlanTreatments.length" class="row">
                    <template v-for="(treatment_plan, index) in patient.activePlanTreatments">
                      <div class="mb-2 col-md-6"
                           v-if="index < 4 || showMorePlans"
                           :key="`treatment-${treatment_plan.id}`">
                        <div class="word-break">
                          <b>{{ treatment_plan.message }}</b>
                          (<span v-if="treatment_plan.signed_at">{{ $t('signed') }}</span><span v-else>{{ $t('not_signed') }}</span>
                          / {{ $t(treatment_plan.status === 'confirmed' ? 'confirmed_male' : treatment_plan.status) }})
                        </div>
                        <div v-for="visit in treatment_plan.visits"
                             :key="`vis-${visit.id}`"
                             class="visit-line">
                          <div class="d-flex justify-content-between align-items-center">
                            <div class="word-break">
                              {{ visit.fullTitle }}
                              ({{ $t(visit.manual_status === 'confirmed' ? 'confirmed_male' : visit.manual_status) }}
                              <span class="circle" :class="`status-${visit.manual_status}`"></span>
                              <span v-if="visit.doctor" class=""> / {{ visit.doctor.full_name }}</span>)
                            </div>
                          </div>
                        </div>
                      </div>
                    </template>
                    <div class="col-12"
                         v-if="!showMorePlans && patient.activePlanTreatments.length >= 4"
                         @click="showMorePlans = true">
                      <a class="pointer text-primary">
                        {{ $t('show_more') }}
                      </a>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </spaceduck-collapse>
      </div>
    </div>
    <PatientSMSModal :specified-phone="selectedPhone"/>
  </div>
</template>

<script setup>
import DepositByAccountsTable from "@/components/parts/patients/DepositByAccountsTable.vue"
import PatientColInline from "@/components/parts/patients/PatientColInline.vue"
import PatientAddTask from "@/components/parts/patients/PatientAddTask.vue"
import SmsSvg from "@/assets/svg-vue/general/sms_t.svg.vue"
import InfoSvg from "@/assets/svg-vue/patients/arrow_down_circle.svg.vue"
import PatientImg from "@/components/parts/patients/PatientImg.vue"
import PlusSvg from "@/assets/svg-vue/patients/plus_tag.svg.vue"
import DollarSvg from "@/assets/svg-vue/patients/dollar.svg.vue"
import OutgoingCallElement from "@/components/parts/patients/OutgoingCallElement.vue"
import ClockSvg from "@/assets/svg-vue/calendar/clock2.svg.vue"
import PhoneSvg from "@/assets/svg-vue/header/phone_t.svg.vue"
import WhatsappTelegramIcon from "@/components/pages/patients/WhatsappTelegramIcon.vue"
import SpaceduckCollapse from "@/components/parts/general/SpaceduckCollapse.vue"
import {computed, getCurrentInstance, nextTick, onBeforeMount, onMounted, ref} from "vue"
import {useRoute} from "vue-router"
import {useStore} from "vuex"
import {formatCurrency, formatDate } from "@/extensions/filters/filters"
import { EloquentService } from "@/services/api/eloquent.api.service"
import { PatientService } from "@/services/api/patient.api.service"
import {countries} from "@/dictionaries/dictionary"
import PatientSMSModal from "@/components/parts/patients/PatientSMSModal.vue"
import LaboratoryOrdersDatatableActions
  from "@/components/parts/datatable/actionbuttons/laboratories/LaboratoryOrdersDatatableActions.vue"
import PatientAddToFunnelBtn from "@/components/parts/patients/PatientAddToFunnelBtn.vue"
import PatientTags from "@/components/parts/patients/PatientTags.vue"

const route = useRoute()
const store = useStore()
const instance = getCurrentInstance()

const treatmentCard = computed(() => ['PatientVisitPage'].includes(route.name))

defineProps({
  patientLoaded: {
    type: Boolean,
    default: false,
  }
})

const clinic = computed(() => store.state.auth.clinic)
const branchId = computed(() => store.state.auth.branch)
const patient = computed(() => store.state.patient.patient)
const isPatient = computed(() => store.getters.isPatient)
const usesBonuses = computed(() => store.getters.usesBonuses)
const isMobile = computed(() => store.state.dom.isMobile)
const rights = computed(() => store.state.auth.rights)
// const loading = computed(() => store.state.dom.loading)
const patientTags = computed(() => patient.value?.tags ? patient.value.tags.map(el => el.id) : [])
const debtForBranch = computed(() => {
  if(!patient.value) return 0
  if(patient.value.debt_calculated_branched) {
    return patient.value.debt_calculated_branched[branchId.value]
  }
  return patient.value.debt_calculated
})

const tags = ref([])
const selectedPhone = ref(null)
const showMorePlans = ref(false)

let $bvModal = null
let i18n = null
let noty = null

const toggleTag = (tag, action = 'add', index = null) => {
  if(action === 'remove') {
    if(!confirm(i18n.t('confirm_tag_deletion'))) return
  }
  PatientService.toggleTag(patient.value.id, tag.id).then(() => {
    if(action === 'add') {
      patient.value.tags.push(tag)
    }
    if(action === 'remove' && index !== null) {
      patient.value.tags.splice(index, 1)
    }
  })
}

const restorePatient = async () => {
  await EloquentService.restore('patient', patient.value.id)
  await store.dispatch('getPatient', patient.value.id)
  noty.info(i18n.t('success_message'))
}

const smsModal = async (phone = null) => {
  selectedPhone.value = phone
  await nextTick()
  $bvModal.show('patient-sms-modal')
}

const sendMail = () => $bvModal.show('send-mail-modal')

const paymentPressed = () => $bvModal.show('patient-topay-modal')

const balanceRefill = () => $bvModal.show('refill-balance-modal')

const bonusRefill = () => $bvModal.show('refill-bonus-modal')

onBeforeMount(() => {
  i18n = instance?.ctx?.$i18n
  $bvModal = instance?.ctx?._bv__modal
  noty = instance?.appContext?.app?.noty
})

onMounted(() => {
  EloquentService.autocomplete('client_tag', '', 999).then(res => {
    tags.value = res.data.result
  })
})
</script>

<style scoped lang="scss">
.patient-card {
  padding: 20px;
  font-size: 16px;
  margin-bottom: 20px;
  margin-top: 50px;
  background: #f4f8fb;
  border-radius: 14px;
}
.card-p {
  background: white;
  border-radius: 14px;
  padding: 17px;
  height: 100%;
}
h2 {
  font-size: 22px;
  margin-bottom: 0;
  font-weight: bold;
}
.p-img {
  position: relative;
  top: -50px;
}
.mphone {
  font-size: 16px;
}
.left-separator {
  background: #D9D9D9;
  width: 1px;
  height: 25px;
  display: inline-block;
  margin-right: 20px;
  vertical-align: top;
}
.phone-comment {
  max-width: 200px;
  font-style: italic;
  color: #273849;
}
.telegram-bot-icon {
  img {
    width: 20px;
    height: 20px;
  }
}

@media screen and (max-width: 1200px) {
  .left-separator {
    display: none;
  }
}

@media screen and (max-width: 992px) {
  .p-img {
    margin-bottom: -40px;
  }
  .mphone {
    font-size: 14px;
  }
}

@media screen and (max-width: 768px) {
  .patient-card {
    font-size: 12px;
    margin-bottom: 20px;
    padding-bottom: 0;
  }
  h2 {
    font-size: 18px;
    margin-bottom: 8px;
  }
  .card-p {
    padding: 10px;
  }
  .more-info-link {
    color: $gray-text;
    font-weight: bold;
    margin-top: 10px;
    padding-top: 12px;
    padding-bottom: 12px;
    border-top: 1px solid $calendar-border-color;
  }
  .mphone {
    font-size: 12px;
  }
  .p-img {
    display: inline-block;
  }
}
</style>

